import React, { useMemo, useState } from 'react';
import { Calculator, Info } from 'lucide-react';

const WASTAGE_KEY = 'dt_wastage_flags_v1';

const readWastageFlags = () => {
  try { return JSON.parse(localStorage.getItem(WASTAGE_KEY) || '{}'); } catch { return {}; }
};
const writeWastageFlag = (productId, on) => {
  try {
    const flags = readWastageFlags();
    if (on) flags[productId] = true; else delete flags[productId];
    localStorage.setItem(WASTAGE_KEY, JSON.stringify(flags));
  } catch { /* localStorage may be unavailable in some browsers */ }
};

// Multipliers to metres. Everything is normalised to metres before we
// multiply length × width to get square metres — one source of truth for
// the conversion table so a future unit (yards, cm) can be added in one
// place.
const UNIT_TO_METRES = { m: 1, ft: 0.3048, in: 0.0254, mm: 0.001 };
const UNIT_LABEL = { m: 'Meters (m)', ft: 'Feet (ft)', in: 'Inches (in)', mm: 'Millimetres (mm)' };

/**
 * Area calculator — sizing helper for products sold in boxes covering a
 * fixed sqm area (flooring, wall panels, decking, ceiling cladding, etc.).
 *
 * Two input modes:
 *   1. "Total area"    → user knows the sqm directly.
 *   2. "Length × Width" → user has dimensions; pick a unit (m / ft / in /
 *      mm), we convert to metres and multiply to derive sqm.
 *
 * The calculator is INFORMATIONAL only. Pressing "Apply to Order" pushes
 * the computed box count up to the parent's quantity selector via
 * `onApplyQty`. The customer still has to press the standard "Add to
 * basket" button to commit — so bulk / tier / CRM pricing runs against the
 * quantity that is actually in the cart line, never a calculator-forced
 * value.
 *
 * How prices work
 * ---------------
 * `product.price` is the **price per box** (source of truth).
 * `product.coverage_per_box_sqm` tells us how many square metres one box
 * covers. From those two we derive the per-sqm rate shown as a secondary
 * label. We never store the derived value — one source of truth.
 *
 * Wastage
 * -------
 * When the user ticks "Add 10%", we multiply the entered/derived area by
 * 1.10 BEFORE dividing by coverage_per_box_sqm — this is the industry
 * standard and matches how B&Q / Wickes / Topps Tiles calculators behave.
 *
 * The wastage flag is persisted per product id in localStorage so the cart
 * line can show the "Includes 10% wastage" note without needing schema
 * changes on the cart itself.
 */
const FlooringCalculator = ({ product, onApplyQty }) => {
  const coverage = Number(product.coverage_per_box_sqm) || 0;
  const pricePerBox = Number(product.price) || 0;
  const pricePerSqm = coverage > 0 ? pricePerBox / coverage : 0;

  const [mode, setMode] = useState('area');            // 'area' | 'dims'
  const [area, setArea] = useState('');                // used when mode === 'area'
  const [length, setLength] = useState('');            // used when mode === 'dims'
  const [width, setWidth] = useState('');
  const [unit, setUnit] = useState('m');
  const [wastage, setWastage] = useState(true);        // recommended default

  // Raw sqm before wastage. Same result regardless of input mode so the
  // downstream math stays in one place.
  const rawSqm = useMemo(() => {
    if (mode === 'dims') {
      const L = parseFloat(length);
      const W = parseFloat(width);
      const m = UNIT_TO_METRES[unit] || 1;
      if (!L || !W || L <= 0 || W <= 0) return 0;
      return +(L * m * W * m);
    }
    const v = parseFloat(area);
    return v > 0 ? v : 0;
  }, [mode, area, length, width, unit]);

  const calc = useMemo(() => {
    if (rawSqm <= 0 || coverage <= 0) return null;
    const adjusted = wastage ? rawSqm * 1.10 : rawSqm;
    const boxes = Math.ceil(adjusted / coverage);
    const totalCoverage = +(boxes * coverage).toFixed(2);
    const totalPrice = +(boxes * pricePerBox).toFixed(2);
    return {
      rawInput: +rawSqm.toFixed(2),
      adjusted: +adjusted.toFixed(2),
      boxes,
      totalCoverage,
      totalPrice,
    };
  }, [rawSqm, wastage, coverage, pricePerBox]);

  const handleApply = () => {
    if (!calc || calc.boxes < 1) return;
    writeWastageFlag(product.id, wastage);
    onApplyQty?.(calc.boxes);
  };

  const modeBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setMode(id)}
      data-testid={`area-calc-mode-${id}`}
      className={`flex-1 text-xs font-bold uppercase tracking-wide py-2 rounded-sm border transition-colors ${
        mode === id
          ? 'bg-[#F46A25] text-white border-[#F46A25]'
          : 'bg-white text-[#12263A] border-neutral-300 hover:border-[#F46A25]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mt-4 border-t border-neutral-200 pt-4" data-testid="area-calculator">
      <div className="mb-2 flex items-center gap-2 text-[#12263A]">
        <Calculator className="w-4 h-4 text-[#F46A25]" />
        <h3 className="text-sm font-bold uppercase tracking-wide">Area calculator</h3>
      </div>
      <p className="text-xs text-neutral-500 mb-3">
        Enter the total area you need — or the room's length &amp; width — and we'll work out how many boxes to buy.
      </p>

      <div className="flex gap-2 mb-3">
        {modeBtn('area', 'Total area')}
        {modeBtn('dims', 'Length × Width')}
      </div>

      {mode === 'area' ? (
        <div className="flex flex-col sm:flex-row gap-3 items-stretch mb-3">
          <label className="flex-1">
            <span className="text-[11px] uppercase text-neutral-500 font-bold">Total area needed (sqm)</span>
            <input
              type="number" step="0.1" min="0"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="e.g. 24.5"
              data-testid="area-calc-total-input"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-lg font-bold focus:outline-none focus:border-[#F46A25]"
            />
          </label>
          <div className="flex-1 bg-neutral-50 border border-neutral-200 rounded-sm p-3">
            <div className="text-[10px] uppercase text-neutral-500 font-bold">Per box covers</div>
            <div className="text-lg font-black text-[#12263A]">{coverage.toFixed(2)} sqm</div>
            <div className="text-[10px] uppercase text-neutral-500 font-bold mt-1">Per sqm</div>
            <div className="text-lg font-black text-[#12263A]">£{pricePerSqm.toFixed(2)}</div>
          </div>
        </div>
      ) : (
        <div className="mb-3 space-y-2">
          <label className="block">
            <span className="text-[11px] uppercase text-neutral-500 font-bold">Measurement unit</span>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              data-testid="area-calc-unit-select"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm font-semibold bg-white focus:outline-none focus:border-[#F46A25]"
            >
              {Object.entries(UNIT_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-[11px] uppercase text-neutral-500 font-bold">Length ({unit})</span>
              <input
                type="number" step="0.01" min="0"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                placeholder={unit === 'mm' ? '4200' : unit === 'in' ? '160' : '4.2'}
                data-testid="area-calc-length-input"
                className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-lg font-bold focus:outline-none focus:border-[#F46A25]"
              />
            </label>
            <label>
              <span className="text-[11px] uppercase text-neutral-500 font-bold">Width ({unit})</span>
              <input
                type="number" step="0.01" min="0"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder={unit === 'mm' ? '3000' : unit === 'in' ? '120' : '3.0'}
                data-testid="area-calc-width-input"
                className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-lg font-bold focus:outline-none focus:border-[#F46A25]"
              />
            </label>
          </div>
          {rawSqm > 0 && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-2 text-xs text-neutral-700" data-testid="area-calc-derived">
              Calculated area: <b className="text-[#12263A]">{rawSqm.toFixed(2)} SQM</b>
              <span className="text-neutral-500"> ({length} × {width} {unit})</span>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] text-neutral-600 bg-orange-50 border border-orange-100 rounded-sm px-2 py-1.5">
            <span>Per box covers</span>
            <b className="text-[#12263A]">{coverage.toFixed(2)} sqm</b>
            <span>·</span>
            <span>Per sqm</span>
            <b className="text-[#12263A]">£{pricePerSqm.toFixed(2)}</b>
          </div>
        </div>
      )}

      <label className="flex items-start gap-2 text-sm cursor-pointer mb-3 select-none">
        <input
          type="checkbox" checked={wastage}
          onChange={(e) => setWastage(e.target.checked)}
          data-testid="area-calc-wastage-toggle"
          className="mt-0.5 w-4 h-4 accent-[#F46A25]"
        />
        <span className="text-[#12263A]">Add 10% for cutting &amp; wastage <span className="text-[#F46A25] font-bold">(Recommended)</span></span>
      </label>

      {calc && wastage && (
        <div className="text-[11px] bg-orange-50 border-l-2 border-[#F46A25] text-neutral-700 p-2 rounded-sm mb-3 flex items-start gap-2" data-testid="area-calc-wastage-note">
          <Info className="w-3 h-3 mt-0.5 text-[#F46A25] flex-shrink-0" />
          <div>
            We've added <b>10% ({(calc.adjusted - calc.rawInput).toFixed(2)} sqm)</b> to your area to account for cutting and wastage. New total: <b>{calc.adjusted} sqm</b>.
          </div>
        </div>
      )}

      {calc && (
        <div className="bg-[#12263A] text-white rounded-sm p-3 mb-3" data-testid="area-calc-result">
          <div className="text-[10px] uppercase text-white/60 font-bold">Calculated area</div>
          <div className="text-lg font-black">{calc.rawInput.toFixed(2)} <span className="text-xs font-semibold text-white/70">SQM</span></div>
          <div className="text-[10px] uppercase text-white/60 font-bold mt-2">You'll need</div>
          <div className="text-2xl font-black">{calc.boxes} <span className="text-sm font-semibold text-white/70">{calc.boxes === 1 ? 'box' : 'boxes'}</span></div>
          <div className="text-[11px] text-white/70 mt-1">
            Covers <b className="text-white">{calc.totalCoverage} sqm</b> · Est. total <b className="text-[#F46A25]">£{calc.totalPrice.toFixed(2)}</b>
          </div>
          <div className="text-[10px] text-white/50 mt-1">Estimate only — bulk / trade pricing applies once added to basket.</div>
        </div>
      )}

      <button
        type="button"
        onClick={handleApply}
        disabled={!calc || calc.boxes < 1}
        data-testid="area-calc-apply-btn"
        className="w-full bg-[#12263A] hover:bg-[#F46A25] disabled:bg-neutral-300 text-white font-bold py-3 rounded-sm flex items-center justify-center gap-2 transition-colors"
      >
        <Calculator className="w-4 h-4" />
        {calc ? `Apply ${calc.boxes} ${calc.boxes === 1 ? 'box' : 'boxes'} to order` : (mode === 'dims' ? 'Enter length & width to calculate' : 'Enter area to calculate boxes')}
      </button>
      <p className="text-[11px] text-neutral-500 mt-2 text-center">
        This only fills in the quantity above — change it any time before adding to basket.
      </p>
    </div>
  );
};

export const isFlooringProduct = (p) => !!(p && Number(p.coverage_per_box_sqm) > 0);
export const readWastageFlag = (pid) => !!readWastageFlags()[pid];

export default FlooringCalculator;
